import { useCallback, useEffect, useMemo, useState } from 'react';
import { equipesApi } from '../../api/resources';
import { _inp } from '../../components/ui/Inputs.jsx';
import { nomComplet } from '../../lib/acces';
import { tr, trf } from '../../lib/i18n';
import { nomDuChantier } from '../../lib/nom-chantier';
import { collaborateursEnPoste } from '../../lib/personnel';
import { C } from '../../theme/palette';
import { equipeDe } from '../../lib/equipes';

/**
 * « Les équipes » : dans une grosse société, chaque technicien (ou la
 * secrétaire) dirige son équipe et ses chantiers. Le patron les crée ici et
 * choisit, pour chaque chef, ce qu'il voit : seulement son équipe, ou toute la
 * société comme aujourd'hui. Le patron, lui, voit toujours tout.
 *
 * <p>Un employé ou un chantier n'appartient qu'à une équipe : le cocher ici le
 * retire de l'autre (le serveur s'en charge).
 */

const idServeur = (id) => typeof id === 'number' && id > 0;

/** « Technicien », « Technicienne » n'est pas devinable : on écrit le rôle. */
const libelleRole = (role) =>
  role === 'SECRETAIRE' ? tr('Secrétaire') : role === 'TECHNICIEN' ? tr('Technicien') : '';

/** Prénom seul, pour les puces. */
const prenom = (e) => e?.prenom || String(nomComplet(e) || e?.nom || '').split(' ')[0];

const styleTitre = { fontSize: 12, fontWeight: 800, color: C.grey, letterSpacing: 0.5 };
const styleCarte = {
  background: C.white,
  border: '1px solid ' + C.border,
  borderRadius: 14,
  padding: 12,
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
};
const styleChamp = { ..._inp, fontSize: 14, minHeight: 44, boxSizing: 'border-box' };
const styleLabel = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  fontSize: 12,
  fontWeight: 700,
  color: C.grey,
};

function Puce({ on, onClick, children, label }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      aria-label={label}
      onClick={onClick}
      style={{
        padding: '9px 12px',
        minHeight: 40,
        borderRadius: 99,
        border: on ? 'none' : '1px solid ' + C.border,
        background: on ? C.teal : C.white,
        color: on ? C.white : C.ink,
        fontSize: 13,
        fontWeight: 700,
        cursor: 'pointer',
      }}
    >
      {on ? '✓ ' : ''}
      {children}
    </button>
  );
}

/** Une équipe vide, prête à remplir. */
const vide = () => ({
  id: null,
  nom: '',
  chefId: '',
  perimetre: 'EQUIPE',
  employeIds: [],
  chantierIds: [],
});

/**
 * Créer ou régler une équipe : nom, chef, employés, chantiers, ce qu'il voit.
 */
function EditeurEquipe({ depart, equipes, chefs, personnes, chantiers, onFini, onAnnule }) {
  const [f, setF] = useState(() => ({
    ...vide(),
    ...depart,
    chefId: depart?.chefId ?? '',
    employeIds: depart?.employeIds || [],
    chantierIds: depart?.chantierIds || [],
  }));
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');
  const [aSupprimer, setASupprimer] = useState(false);
  const autres = equipes.filter((q) => q.id !== f.id);
  const chef = chefs.find((c) => c.id === Number(f.chefId));
  const prenomChef = chef ? String(chef.nom || '').split(' ')[0] : '';

  const bascule = (cle, id) =>
    setF((p) => ({
      ...p,
      [cle]: p[cle].includes(id) ? p[cle].filter((x) => x !== id) : [...p[cle], id],
    }));

  const enregistre = async () => {
    if (!f.nom.trim()) {
      setErreur(tr("Donne un nom à l'équipe."));
      return;
    }
    setEnvoi(true);
    setErreur('');
    const corps = {
      nom: f.nom.trim(),
      chefId: f.chefId === '' ? null : Number(f.chefId),
      perimetre: f.perimetre,
      employeIds: f.employeIds,
      chantierIds: f.chantierIds,
    };
    try {
      if (f.id) await equipesApi.metAJour(f.id, corps);
      else await equipesApi.cree(corps);
      onFini();
    } catch (e) {
      setErreur(e?.message || tr("L'équipe n'a pas pu être enregistrée."));
    } finally {
      setEnvoi(false);
    }
  };

  const supprime = async () => {
    setEnvoi(true);
    try {
      await equipesApi.supprime(f.id);
      onFini();
    } catch (e) {
      setErreur(e?.message || tr("L'équipe n'a pas pu être supprimée."));
      setEnvoi(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <label style={styleLabel}>
        {tr("Nom de l'équipe")}
        <input
          value={f.nom}
          onChange={(e) => setF((p) => ({ ...p, nom: e.target.value }))}
          placeholder={tr('Ex. : Équipe de Marc')}
          maxLength={120}
          style={styleChamp}
        />
      </label>
      <label style={styleLabel}>
        {tr("Chef d'équipe")}
        <select
          value={f.chefId}
          onChange={(e) => setF((p) => ({ ...p, chefId: e.target.value }))}
          style={styleChamp}
        >
          <option value="">{tr('— Choisir un technicien ou la secrétaire —')}</option>
          {chefs.map((c) => (
            <option key={c.id} value={c.id}>
              {c.nom} · {libelleRole(c.role)}
            </option>
          ))}
        </select>
      </label>
      {chefs.length === 0 && (
        <div style={{ fontSize: 12, color: C.grey, lineHeight: 1.45 }}>
          {tr(
            "Aucun technicien ni secrétaire n'a encore de compte. Invite-le d'abord (sa fiche → « Inviter »), puis reviens ici.",
          )}
        </div>
      )}

      <div style={styleTitre}>{tr('SES EMPLOYÉS')}</div>
      {personnes.length === 0 ? (
        <div style={{ fontSize: 12.5, color: C.grey }}>{tr('Aucun employé actif.')}</div>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {personnes.map((e) => {
            const ailleurs = equipeDe(autres, 'employe', e.id);
            return (
              <Puce
                key={e.id}
                on={f.employeIds.includes(e.id)}
                onClick={() => bascule('employeIds', e.id)}
              >
                {prenom(e)}
                {ailleurs && !f.employeIds.includes(e.id) ? ` (${ailleurs.nom})` : ''}
              </Puce>
            );
          })}
        </div>
      )}

      <div style={styleTitre}>{tr('SES CHANTIERS')}</div>
      {chantiers.length === 0 ? (
        <div style={{ fontSize: 12.5, color: C.grey }}>{tr('Aucun chantier en cours.')}</div>
      ) : (
        <div style={{ ...styleCarte, padding: 0, gap: 0, overflow: 'hidden' }}>
          {chantiers.map((c, i) => {
            const on = f.chantierIds.includes(c.id);
            const ailleurs = equipeDe(autres, 'chantier', c.id);
            return (
              <label
                key={c.id}
                style={{
                  padding: '11px 12px',
                  display: 'flex',
                  gap: 10,
                  alignItems: 'center',
                  borderTop: i ? '1px solid ' + C.border : 'none',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => bascule('chantierIds', c.id)}
                  style={{ width: 20, height: 20, flexShrink: 0 }}
                />
                <span
                  style={{ fontSize: 14, fontWeight: on ? 700 : 500, color: on ? C.ink : C.grey }}
                >
                  {nomDuChantier(c, chantiers)}
                  {ailleurs && !on ? ` (${ailleurs.nom})` : ''}
                </span>
              </label>
            );
          })}
        </div>
      )}

      <div style={styleTitre}>
        {prenomChef ? trf('CE QUE {0} VOIT', prenomChef.toUpperCase()) : tr('CE QUE LE CHEF VOIT')}
      </div>
      <div style={{ ...styleCarte, gap: 10 }}>
        {[
          [
            'EQUIPE',
            tr('Seulement son équipe et ses chantiers'),
            tr('Messages, plans, rendez-vous, planning, congés, formations de ses employés'),
          ],
          ['TOUT', tr('Toute la société'), tr("Comme aujourd'hui")],
        ].map(([val, titre, detail]) => (
          <label
            key={val}
            style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 14 }}
          >
            <input
              type="radio"
              name="perimetre-equipe"
              checked={f.perimetre === val}
              onChange={() => setF((p) => ({ ...p, perimetre: val }))}
              style={{ width: 20, height: 20, marginTop: 1, flexShrink: 0 }}
            />
            <span>
              <b>{titre}</b>
              <br />
              <span style={{ fontSize: 12, color: C.grey }}>{detail}</span>
            </span>
          </label>
        ))}
      </div>

      {erreur && (
        <div
          role="alert"
          style={{
            fontSize: 12.5,
            color: C.red,
            background: C.redL,
            padding: '8px 10px',
            borderRadius: 10,
          }}
        >
          {erreur}
        </div>
      )}
      <button
        type="button"
        onClick={enregistre}
        disabled={envoi}
        style={{
          border: 'none',
          background: C.green,
          color: C.white,
          borderRadius: 14,
          padding: 15,
          fontSize: 15,
          fontWeight: 800,
          cursor: envoi ? 'wait' : 'pointer',
          opacity: envoi ? 0.7 : 1,
        }}
      >
        {envoi ? tr('Enregistrement…') : tr("Enregistrer l'équipe")}
      </button>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={onAnnule}
          style={{
            flex: 1,
            minHeight: 44,
            borderRadius: 12,
            border: '1px solid ' + C.border,
            background: C.white,
            fontSize: 13,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          {tr('Annuler')}
        </button>
        {f.id &&
          (aSupprimer ? (
            <button
              type="button"
              onClick={supprime}
              disabled={envoi}
              style={{
                flex: 1,
                minHeight: 44,
                borderRadius: 12,
                border: 'none',
                background: C.red,
                color: C.white,
                fontSize: 13,
                fontWeight: 800,
                cursor: 'pointer',
              }}
            >
              {tr("Oui, supprimer l'équipe")}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setASupprimer(true)}
              style={{
                flex: 1,
                minHeight: 44,
                borderRadius: 12,
                border: '1px solid ' + C.red,
                background: C.white,
                color: C.red,
                fontSize: 13,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              {tr("Supprimer l'équipe")}
            </button>
          ))}
      </div>
      {aSupprimer && (
        <div style={{ fontSize: 12, color: C.grey, lineHeight: 1.45 }}>
          {tr(
            'Ses employés et ses chantiers ne sont pas supprimés : ils redeviennent « sans équipe ». Les messages déjà envoyés restent.',
          )}
        </div>
      )}
    </div>
  );
}

/**
 * La liste des équipes (patron) : une carte par équipe, « Sans équipe »,
 * « + Créer une équipe ».
 *
 * @param {object} props
 * @param {object[]} props.employes les employés de la société
 * @param {object[]} props.chantiers les chantiers de la société
 * @param {(equipes: object[]) => void} [props.onChange] prévient quand les équipes changent
 */
export function LesEquipes({ employes = [], chantiers = [], onChange }) {
  const [equipes, setEquipes] = useState([]);
  const [chefs, setChefs] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [edite, setEdite] = useState(null);

  const personnes = useMemo(
    () => collaborateursEnPoste(employes).filter((e) => idServeur(e.id)),
    [employes],
  );
  const chantiersEnCours = useMemo(
    () =>
      (chantiers || []).filter(
        (c) => c && idServeur(c.id) && c.statut !== 'Terminé' && c.statut !== 'Annulé',
      ),
    [chantiers],
  );

  const charge = useCallback(async () => {
    try {
      const [l, c] = await Promise.all([equipesApi.liste(), equipesApi.chefsPossibles()]);
      const liste = Array.isArray(l) ? l : [];
      setEquipes(liste);
      setChefs(Array.isArray(c) ? c : []);
      setErreur('');
      onChange?.(liste);
    } catch (e) {
      setErreur(e?.message || tr("Les équipes n'ont pas pu être chargées."));
    } finally {
      setChargement(false);
    }
  }, [onChange]);

  useEffect(() => {
    charge();
  }, [charge]);

  if (edite) {
    return (
      <EditeurEquipe
        depart={edite}
        equipes={equipes}
        chefs={chefs}
        personnes={personnes}
        chantiers={chantiersEnCours}
        onAnnule={() => setEdite(null)}
        onFini={() => {
          setEdite(null);
          charge();
        }}
      />
    );
  }

  const nomsDe = (ids) =>
    ids
      .map((id) => personnes.find((e) => e.id === id))
      .filter(Boolean)
      .map(prenom)
      .join(', ');
  const sansEquipeEmp = personnes.filter((e) => !equipeDe(equipes, 'employe', e.id));
  const sansEquipeCh = chantiersEnCours.filter((c) => !equipeDe(equipes, 'chantier', c.id));
  const compte = (nbE, nbC) => trf('{0} employé(s) · {1} chantier(s)', nbE, nbC);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ fontSize: 12.5, color: C.grey, lineHeight: 1.45 }}>
        {tr('Chaque technicien gère son équipe et ses chantiers. Toi, tu vois tout.')}
      </div>
      {chargement && <div style={{ fontSize: 12.5, color: C.grey }}>{tr('Chargement…')}</div>}
      {erreur && (
        <div role="alert" style={{ fontSize: 12.5, color: C.red }}>
          {erreur}
        </div>
      )}
      {equipes.map((q) => {
        const emp = (q.employeIds || []).filter((id) => personnes.some((e) => e.id === id));
        const ch = (q.chantierIds || []).filter((id) => chantiersEnCours.some((c) => c.id === id));
        return (
          <button
            key={q.id}
            type="button"
            onClick={() => setEdite(q)}
            style={{ ...styleCarte, textAlign: 'left', cursor: 'pointer', font: 'inherit' }}
          >
            <span
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 8,
                width: '100%',
              }}
            >
              <span style={{ fontSize: 15, fontWeight: 800, color: C.ink }}>{q.nom}</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: C.teal }}>
                {libelleRole(q.chefRole)}
              </span>
            </span>
            <span style={{ fontSize: 13, color: C.ink }}>
              {q.chefNom
                ? trf("Chef d'équipe : {0}", q.chefNom)
                : tr("Pas encore de chef d'équipe")}
            </span>
            <span style={{ fontSize: 13, color: C.grey }}>{compte(emp.length, ch.length)}</span>
            {emp.length > 0 && <span style={{ fontSize: 12, color: C.grey }}>{nomsDe(emp)}</span>}
            <span
              style={{
                fontSize: 12,
                color: q.perimetre === 'TOUT' ? C.grey : C.green,
                background: q.perimetre === 'TOUT' ? C.bg : C.greenL,
                padding: '6px 8px',
                borderRadius: 8,
              }}
            >
              {q.perimetre === 'TOUT'
                ? tr('Le chef voit toute la société')
                : tr('Le chef voit seulement son équipe et ses chantiers')}
            </span>
          </button>
        );
      })}
      {!chargement && (
        <div style={{ ...styleCarte, border: '1.5px dashed ' + C.border }}>
          <div style={{ fontSize: 15, fontWeight: 800 }}>{tr('Sans équipe')}</div>
          <div style={{ fontSize: 13, color: C.grey }}>
            {compte(sansEquipeEmp.length, sansEquipeCh.length)}
          </div>
          {sansEquipeEmp.length > 0 && (
            <div style={{ fontSize: 12, color: C.grey }}>
              {sansEquipeEmp.map(prenom).join(', ')}
            </div>
          )}
          <div style={{ fontSize: 12, color: C.grey }}>
            {tr('Gérés par toi et par ceux qui voient toute la société')}
          </div>
        </div>
      )}
      <button
        type="button"
        onClick={() => setEdite(vide())}
        style={{
          border: 'none',
          background: C.teal,
          color: C.white,
          borderRadius: 14,
          padding: 15,
          fontSize: 15,
          fontWeight: 800,
          cursor: 'pointer',
        }}
      >
        {tr('+ Créer une équipe')}
      </button>
      <div style={{ fontSize: 12, color: C.grey, lineHeight: 1.5 }}>
        {tr(
          'La secrétaire peut rester « toute la société » ou avoir aussi son équipe. Ce que le chef crée (employé, chantier) entre dans son équipe.',
        )}
      </div>
    </div>
  );
}
